import {
  AppBar,
  Avatar,
  Box,
  Button,
  IconButton,
  Toolbar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { ExternalLink } from 'lucide-react';
import React from 'react';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import InputIcon from '@mui/icons-material/Input';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import SearchBar from '../../components/search_bar';
import permissionManager from '../../lib/permission.manager';
import useAuthProviderStore from '../../stores/auth_provider.store';
import useAppsStore from '../../stores/apps.store';

const Image = styled('img')(({ theme }) => ({
  height: 25,
  [theme.breakpoints.down('sm')]: {
    height: 18,
  },
}));

const ImageTarget = styled('img')(({ theme }) => ({
  height: 45,
  marginLeft: 15,
  [theme.breakpoints.down('sm')]: {
    margin: '0px 10px',
    height: 35,
  },
}));

function TopBar() {
  const { userPermissions, graphProfile, profilePic } = useAuthProviderStore(
    (state) => ({
      userPermissions: state.userPermissions,
      profilePic: state.profilePic,
      graphProfile: state.graphProfile,
    }),
  );

  const { setSearchText } = useAppsStore();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleUserProfileClick = () => {
    handleClose();
    navigate('/apps/user-profile');
  };

  const { signOutUser } = useAuthProviderStore();
  return (
    <AppBar
      sx={{
        height: '60px',
        position: 'sticky',
        backgroundColor: '#FFFFFF',
        color: '#899499',
      }}
      elevation={0}
    >
      <Toolbar
        sx={(theme) => ({
          paddingLeft: 40,
          [theme.breakpoints.down('sm')]: {
            paddingLeft: 1,
          },
        })}
      >
        <Image
          src={`${process.env.PUBLIC_URL}/images/Kmart-logo.png`}
          alt="Kmart"
        />
        <ImageTarget
          src={`${process.env.PUBLIC_URL}/images/targetlogo.png`}
          alt="Target"
        />

        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Box
            sx={(theme) => ({
              marginRight: 10,
              [theme.breakpoints.down('md')]: {
                display: 'none',
              },
              [theme.breakpoints.up('sm')]: {
                display: 'inline-block',
              },
              [theme.breakpoints.down('xl')]: {
                marginRight: 30,
              },
            })}
          >
            <SearchBar
              style={{
                marginBottom: 30,
                width: '400px',
                margin: 'auto',
              }}
              onSearch={(text) => {
                setSearchText(text);
              }}
            />
          </Box>
          <Box sx={{ mx: 2 }}>
            <Button
              variant="outlined"
              size="small"
              endIcon={<ExternalLink size={14} />}
              onClick={() =>
                window.open(process.env.REACT_APP_V1_APPS_URL, '_blank')
              }
              sx={(theme) => ({
                textTransform: 'none',
                borderRadius: '8px',
                whiteSpace: 'nowrap',
                color: '#000',
                borderColor: '#000',
                '&:hover': {
                  color: theme.palette.primary.contrastText,
                  borderColor: theme.palette.primary.main,
                  backgroundColor: 'gray',
                },
              })}
            >
              Try New Version
            </Button>
          </Box>
          <Box
            sx={(theme) => ({
              [theme.breakpoints.down('sm')]: {
                display: 'none',
              },
            })}
            style={{ margin: '8px 20px 0px 10px' }}
          >
            <IconButton onClick={handleProfileClick} size="small">
              {profilePic === null && <Avatar sx={{ width: 36, height: 36 }} />}
              {profilePic === false && (
                <Avatar sx={{ width: 36, height: 36 }}>
                  {graphProfile?.nameInitials}
                </Avatar>
              )}
              {profilePic && (
                <Avatar src={profilePic} sx={{ width: 36, height: 36 }} />
              )}
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  minWidth: 250,
                  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                  borderRadius: 2,
                  mt: 1,
                },
              }}
            >
              <Box sx={{ px: 2, py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {profilePic === null && (
                    <Avatar sx={{ width: 48, height: 48 }} />
                  )}
                  {profilePic === false && (
                    <Avatar sx={{ width: 48, height: 48 }}>
                      {graphProfile?.nameInitials}
                    </Avatar>
                  )}
                  {profilePic && (
                    <Avatar src={profilePic} sx={{ width: 48, height: 48 }} />
                  )}
                  <Box sx={{ ml: 1.5 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {graphProfile?.displayName}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider />

              <MenuItem sx={{ py: 1.5, px: 2 }}>
                <ListItemIcon>
                  <EmailOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  <Typography variant="body2">{graphProfile?.mail}</Typography>
                </ListItemText>
              </MenuItem>

              <MenuItem
                onClick={handleUserProfileClick}
                sx={{ py: 1.5, px: 2 }}
              >
                <ListItemIcon>
                  <PersonOutlineIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  <Typography variant="body2">User Profile</Typography>
                </ListItemText>
              </MenuItem>

              <Divider />

              <MenuItem
                onClick={() => {
                  handleClose();
                  signOutUser();
                }}
                sx={{ py: 1.5, px: 2, color: 'error.main' }}
              >
                <ListItemIcon>
                  <InputIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>
                  <Typography variant="body2">Logout</Typography>
                </ListItemText>
              </MenuItem>
            </Menu>
          </Box>

          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              marginRight: 22,
            }}
          >
            <IconButton
              sx={[
                () => ({
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }),
              ]}
              onClick={() => {
                window.open(
                  'https://forms.office.com/Pages/ResponsePage.aspx?id=A8qoy5WLjUSyWZikTREvfGdnCHj_-gBOjyKnAPFYWlNURUNWOERIQ01TR05YVVhWSjdUQzNUSzdKVi4u&ra3118a628d36492abc80139f2533da8e=%22Supplier%20Portal%22',
                );
              }}
              size="large"
            >
              <FeedbackOutlinedIcon
                sx={(theme) => ({
                  marginBottom: '-16px',
                  color: '#000',
                  fontSize: 30,
                  [theme.breakpoints.down('lg')]: {
                    fontSize: 30,
                    marginBottom: '-12px',
                  },
                })}
              />
            </IconButton>
            <Typography
              variant="subtitle2"
              sx={(theme) => ({
                [theme.breakpoints.down('lg')]: {
                  fontSize: 11,
                },
              })}
            >
              Feedback
            </Typography>
          </Box>

          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              marginRight: 22,
            }}
          >
            <IconButton
              sx={[
                () => ({
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }),
              ]}
              onClick={() =>
                permissionManager?.isUserSupplier(userPermissions)
                  ? window.open(process.env.REACT_APP_SUPPLIER_SERVICENOW_URL)
                  : window.open(process.env.REACT_APP_KMART_SERVICENOW_URL)
              }
              size="large"
            >
              <BugReportOutlinedIcon
                sx={(theme) => ({
                  marginBottom: '-16px',
                  color: '#000',
                  fontSize: 30,
                  [theme.breakpoints.down('lg')]: {
                    fontSize: 30,
                    marginBottom: '-12px',
                  },
                })}
              />
            </IconButton>
            <Typography
              variant="subtitle2"
              sx={(theme) => ({
                [theme.breakpoints.down('lg')]: {
                  fontSize: 11,
                },
              })}
            >
              Report Issue
            </Typography>
          </Box>
        </div>
      </Toolbar>
    </AppBar>
  );
}

export default TopBar;
